import { useFlag } from '@unleash/proxy-client-vue';
import type { ComputedRef } from 'vue';
import { computed, ref, watch } from 'vue';

import api from '@/api';
import { useParentEntityStore } from '@/stores/ParentEntity';

export interface UseLowestCostReasonCodeResponse {
  /**
   * Whether or not the lowest cost reason code is required for the current domain's configuration
   * and feature flag configuration
   */
  isRateSelectionReasonRequired: ComputedRef<boolean>;
}

export const useLowestCostReasonCode = (): UseLowestCostReasonCodeResponse => {
  const { parentEntity, owningClient } = useParentEntityStore();

  const isUseLowestCostReasonCodeFeatureFlagEnabled = useFlag(
    'lowest-cost-reason-feat-flag',
  );

  const isRateSelectionReasonRequiredForDomain = ref(false);

  const isSwc = (entity: typeof parentEntity.value) =>
    entity && 'swc' in entity && entity.swc;

  const isRateSelectionReasonRequired = computed(
    () =>
      !isSwc(parentEntity.value) &&
      isUseLowestCostReasonCodeFeatureFlagEnabled.value &&
      isRateSelectionReasonRequiredForDomain.value,
  );

  watch(
    () => owningClient.value?.id,
    async (newVal) => {
      isRateSelectionReasonRequiredForDomain.value =
        !!newVal &&
        (await api.rateSelectionReason.isRateSelectionReasonRequired(newVal));
    },
    { immediate: true },
  );

  return {
    isRateSelectionReasonRequired,
  };
};
